import React, { useState, useEffect } from "react";
import "./CreateOwner.css";
import AdminHeader from "../../../Component/AdminHeader/AdminHeader";
import SaveChanges from "../../../Assets/Group 36938.png";
import LocationSelector from "../../../Component/LocationSelector/LocationSelector";
import InputError from "../../../Component/InputError/InputError";
import { useNavigate } from "react-router-dom";


import {
  initialFormState,
  initialLocationState,
  sanitizeInput,
  validationRules,
  validatePasswordMatch,
} from "./CreateOwnerValidation";

import { createOwnerService } from "./Service";
import { useAdminAuth } from "../../../ContextAPI/AdminContex";

// Function to decode JWT token
const decodeJWT = (token) => {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch (e) {
    return null;
  }
};

export default function CreateOwner() {
  const saved = JSON.parse(localStorage.getItem("createOwnerForm")) || {};

  const { admin } = useAdminAuth();                      // 🔥 GET ADMIN TOKEN
  const decoded = admin?.token ? decodeJWT(admin.token) : null;
  const admin_id = decoded?.id || decoded?.sub || null;   // 🔥 EXTRACT ADMIN ID

  const [form, setForm] = useState(initialFormState(saved));
  const [location, setLocation] = useState(initialLocationState(saved));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLocation = (val) => setLocation(val);

  const navigate = useNavigate();


  useEffect(() => {
    localStorage.setItem(
      "createOwnerForm",
      JSON.stringify({ ...form, ...location })
    );
  }, [form, location]);

  const handleChange = (e) => {
    const { name } = e.target;
    const sanitized = sanitizeInput(name, e.target.value);

    setForm({ ...form, [name]: sanitized });

    if (touched[name]) {
      setErrors({
        ...errors,
        [name]: validationRules(name, sanitized, form),
      });
    }

    if (name === "password" || name === "confirmPassword") {
      const matchMsg = validatePasswordMatch(
        name === "password" ? sanitized : form.password,
        name === "confirmPassword" ? sanitized : form.confirmPassword
      );
      setErrors((prev) => ({ ...prev, confirmPassword: matchMsg }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });

    setErrors({
      ...errors,
      [name]: validationRules(name, form[name], form),
    });
  };

  const isFormValid =
    Object.values(errors).every((e) => e === "") &&
    Object.values(form).every((v) => v !== "") &&
    location.country &&
    location.state &&
    location.city &&
    location.address1 &&
    admin_id;                // 🔥 ADMIN ID MUST EXIST

const handleCreate = async () => {
  if (!admin_id) {
    alert("Admin authentication error. Please login again.");
    return;
  }

  setLoading(true);
  setApiError("");

  const payload = {
    admin_id,
    owner_name: form.ownerName,
    company_name: form.companyName,
    address: {
      country: location.country,
      state: location.state,
      city: location.city,
      address_line: location.address1,
    },
    pan_card_number: form.pan,
    gst_number: form.gst,
    device_id: form.deviceId,
    user_name: form.user_name,
    email: form.email,
    phone_number: form.phone,
    pricing_model: form.pricingModel,
    is_active: form.isActivated,
    password: form.password,
  };

  const response = await createOwnerService(payload);
  setLoading(false);

  if (!response.success) {
    setApiError(response.message);
    return;
  }

  alert("Owner Created Successfully!");
  localStorage.removeItem("createOwnerForm");
  navigate("/list-owner");     // 🔥 Redirect here
};


  return (
    <>
      <AdminHeader />

      <div className="create-owner-page">
        <div className="owner-header">
          <h1>Create New Owner</h1>
        </div>

        <div className="owner-card">
          <h3 className="section-title">Owner Information</h3>

          <div className="two-col">
            <div className="form-group">
              <label>Owner Name</label>
              <input
                name="ownerName"
                value={form.ownerName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.ownerName && <InputError message={errors.ownerName} />}
            </div>

            <div className="form-group">
              <label>Unique Name</label>
              <input
                name="user_name"
                value={form.user_name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.user_name && <InputError message={errors.user_name} />}
            </div>
          </div>

          <div className="two-col">
            <div className="form-group">
              <label>Email</label>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.email && <InputError message={errors.email} />}
            </div>

            <div className="form-group phone-field">
              <label>Phone Number</label>
              <div className="phone-container">
                <select
                  value={form.countryCode}
                  onChange={(e) =>
                    setForm({ ...form, countryCode: e.target.value })
                  }
                >
                  <option value="+91">+91</option>
                  <option value="+971">+971</option>
                </select>

                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              {touched.phone && <InputError message={errors.phone} />}
            </div>
          </div>

          <h3 className="section-title">Company & Legal Details</h3>
          <div className="two-col">
            <div className="form-group">
              <label>Company Name</label>
              <input
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.companyName && (
                <InputError message={errors.companyName} />
              )}
            </div>

            <div className="form-group">
              <label>PAN-Card Number</label>
              <input
                name="pan"
                value={form.pan}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.pan && <InputError message={errors.pan} />}
            </div>
          </div>

          <div className="two-col">
            <div className="form-group">
              <label>GST Number</label>
              <input
                name="gst"
                value={form.gst}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.gst && <InputError message={errors.gst} />}
            </div>

            <div className="form-group">
              <label>Device ID</label>
              <input
                name="deviceId"
                value={form.deviceId}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.deviceId && <InputError message={errors.deviceId} />}
            </div>
          </div>

          <h3 className="section-title">Address Details</h3>
          <LocationSelector onChange={handleLocation} />

          <h3 className="section-title">Pricing & Activation</h3>
          <div className="two-col equal-width">
            <div className="form-group">
              <label>Pricing Model</label>
              <select
                value={form.pricingModel}
                onChange={(e) =>
                  setForm({ ...form, pricingModel: e.target.value })
                }
              >
                <option>POSTPAID</option>
              </select>
            </div>

            <div className="toggle-wrap">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={form.isActivated}
                  onChange={() =>
                    setForm({ ...form, isActivated: !form.isActivated })
                  }
                />
                <span className="slider"></span>
              </label>
              <span className="toggle-text">
                {form.isActivated ? "Owner is Active" : "Owner Deactivated"}
              </span>
            </div>
          </div>

          <h3 className="section-title">Security</h3>
          <div className="two-col">
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.password && <InputError message={errors.password} />}
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.confirmPassword && (
                <InputError message={errors.confirmPassword} />
              )}
            </div>
          </div>

          {apiError && <p className="api-error">{apiError}</p>}

          <div className="form-footer">
            <span className="unsaved">
              <img src={SaveChanges} alt="Unsaved" className="unsaved-icon" />
              Unsaved changes stored
            </span>

            <div className="footer-buttons">
              <button
                className="cancel-btn"
                onClick={() => {
                  localStorage.removeItem("createOwnerForm");
                  window.location.reload();
                }}
              >
                Reset
              </button>

              <button
                className="create-btn"
                disabled={!isFormValid || loading}
                onClick={handleCreate}
              >
                {loading ? "Creating..." : "Create Owner"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
